import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAgentPane } from '../../context/AgentPaneContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';
import { aiApi, mcpChatApi } from '../../utils/api';
import MCPDebugLogger from '../../utils/debug';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  mcpData?: any[];
  toolsUsed?: string[];
}

interface ItemCard {
  _id: string;
  title: string;
  description?: string;
  price: number;
  currency: string;
  condition: string;
  images: string[];
  materials?: string[];
  project?: {
    _id: string;
    title: string;
  };
  location?: {
    city: string;
    region: string;
  };
  seller?: {
    name: string;
    email: string;
  };
  quantity?: number;
}

const contextualPrompts = {
  '/': [
    'Help me get started with Deconstructed',
    'What can I sell on this platform?',
    'Show me available bricks',
    'Find timber under $100',
  ],
  '/projects': [
    'How do I create a new project?',
    'What information should I include?',
    'Show me available materials for my project',
    'Find salvaged materials nearby',
  ],
  '/marketplace': [
    'Show me available timber',
    'Find materials under $100',
    'What salvaged materials are available?',
    'Show me materials in Auckland',
    'Find new condition timber',
    'What are the cheapest materials?',
  ],
  '/items': [
    'Help me write a better description',
    'Suggest a fair price for this item',
    'What photos should I take?',
    'Show me similar items available',
  ],
};

export const RightAgentPane: React.FC = () => {
  const { isOpen, close } = useAgentPane();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paneWidth, setPaneWidth] = useState(400); // Default width for desktop
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);
  const streamingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getContextualPrompts = () => {
    const path = location.pathname.startsWith('/items/') ? '/items' : location.pathname;
    return contextualPrompts[path as keyof typeof contextualPrompts] || contextualPrompts['/'];
  };

  // Streaming text effect
  const streamText = (text: string, messageIndex: number, mcpData?: any[], toolsUsed?: string[]) => {
    const words = text.split(' ');
    let currentWordIndex = 0;
    
    // Clear any existing streaming interval
    if (streamingIntervalRef.current) {
      clearInterval(streamingIntervalRef.current);
    }

    streamingIntervalRef.current = setInterval(() => {
      if (currentWordIndex < words.length) {
        const partialText = words.slice(0, currentWordIndex + 1).join(' ');
        
        setMessages(prev => prev.map((msg, index) => 
          index === messageIndex 
            ? { 
                ...msg, 
                content: partialText, 
                isStreaming: true,
                mcpData: currentWordIndex === words.length - 1 ? mcpData : undefined,
                toolsUsed: currentWordIndex === words.length - 1 ? toolsUsed : undefined
              }
            : msg
        ));
        
        currentWordIndex++;
      } else {
        // Streaming complete
        setMessages(prev => prev.map((msg, index) => 
          index === messageIndex 
            ? { ...msg, isStreaming: false, mcpData, toolsUsed }
            : msg
        ));
        
        if (streamingIntervalRef.current) {
          clearInterval(streamingIntervalRef.current);
          streamingIntervalRef.current = null;
        }
      }
    }, 50); // Adjust speed here (50ms per word)
  };

  // Clean up streaming interval on unmount
  useEffect(() => {
    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
      }
    };
  }, []);

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    const newMessages = [...messages, { role: 'user' as const, content: message }];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    // Add placeholder for assistant response
    const assistantMessageIndex = newMessages.length;
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: '', 
      isStreaming: true 
    }]);

    try {
      // Get current page context
      const path = location.pathname.startsWith('/items/') ? '/items' : location.pathname;
      const contextMap: Record<string, string> = {
        '/': 'home page - general marketplace help',
        '/projects': 'projects page - project management and item creation',
        '/marketplace': 'marketplace page - browsing and buying items',
        '/items': 'item detail page - specific item information',
        '/account': 'account page - user settings and history'
      };
      
      const context = contextMap[path] || 'general marketplace assistance';
      
      // Detect if the message is about materials/items that should use MCP
      const materialKeywords = [
        'brick', 'bricks', 'timber', 'wood', 'lumber', 'steel', 'metal', 'concrete',
        'tile', 'tiles', 'door', 'doors', 'window', 'windows', 'fixture', 'fixtures',
        'material', 'materials', 'salvage', 'salvaged', 'available', 'show me',
        'find', 'search', 'looking for', 'need', 'want', 'buy', 'purchase',
        'price', 'cost', 'cheap', 'expensive', 'condition', 'new', 'used'
      ];
      
      const shouldUseMCP = path === '/marketplace' || 
                          materialKeywords.some(keyword => 
                            message.toLowerCase().includes(keyword.toLowerCase())
                          );
      
      // Use MCP chat for material/item queries, regular AI for other queries
      let response;
      const startTime = Date.now();
      
      if (shouldUseMCP) {
        // Use MCP chat service for material search and queries
        MCPDebugLogger.logMCPRequest('/mcp-chat/message', {
          message,
          context: 'marketplace'
        });
        
        try {
          response = await mcpChatApi.sendMessage({
            message,
            context: 'marketplace'
          });
          
          MCPDebugLogger.logMCPResponse('/mcp-chat/message', response.data, Date.now() - startTime);
        } catch (error) {
          MCPDebugLogger.logError('MCP Chat Request', error);
          throw error;
        }
      } else {
        // Use regular AI API for other contexts
        MCPDebugLogger.logOpenAIRequest('gpt-4', [{ role: 'user', content: message }]);
        
        try {
          response = await aiApi.chat(message, context);
          
          MCPDebugLogger.logOpenAIResponse('gpt-4', response.data, Date.now() - startTime);
        } catch (error) {
          MCPDebugLogger.logError('OpenAI Chat Request', error);
          throw error;
        }
      }
      
      if (response.data.success && response.data.data && response.data.data.response) {
        // Extract MCP data if available
        let mcpData = undefined;
        let toolsUsed: string[] | undefined = undefined;
        
        // Check if this was an MCP response (has additional fields)
        if (shouldUseMCP && 'toolsUsed' in response.data.data) {
          toolsUsed = (response.data.data as any).toolsUsed;
          
          // Check if MCP response has structured data
          if ('data' in response.data.data && Array.isArray((response.data.data as any).data)) {
            // Extract actual listing data from MCP tool results
            const toolResults = (response.data.data as any).data;
            const listingData: ItemCard[] = [];
            
            for (const toolResult of toolResults) {
              if (toolResult.result && Array.isArray(toolResult.result)) {
                // Convert listing data to ItemCard format
                const items = toolResult.result.map((listing: any) => ({
                  _id: listing.id || listing._id,
                  title: listing.title,
                  description: listing.description,
                  price: listing.price,
                  currency: 'NZD', // Default for NZ marketplace
                  condition: listing.condition,
                  images: listing.images || [],
                  materials: listing.materials || (listing.category ? [listing.category] : []),
                  project: listing.project ? {
                    _id: listing.project._id || listing.project.id,
                    title: listing.project.title
                  } : undefined,
                  location: listing.location ? {
                    city: listing.location.city,
                    region: listing.location.region
                  } : undefined,
                  seller: listing.seller ? {
                    name: listing.seller.name,
                    email: listing.seller.email
                  } : undefined,
                  quantity: listing.quantity || listing.availableQuantity
                }));
                
                listingData.push(...items);
              }
            }
            
            if (listingData.length > 0) {
              mcpData = listingData;
            }
          }
        }
        
        // Start streaming the response
        streamText(response.data.data.response, assistantMessageIndex, mcpData, toolsUsed);
      } else {
        throw new Error('AI response failed');
      }
    } catch (error: any) {
      console.error('AI chat error:', error);
      
      // Fallback response with streaming
      const fallbackResponse = `I'm having trouble connecting to my AI service right now. However, I can still help you with general questions about the Deconstructed marketplace. Try asking about creating projects, listing items, or finding sustainable building materials.`;
      
      streamText(fallbackResponse, assistantMessageIndex);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptClick = (prompt: string) => {
    handleSendMessage(prompt);
  };

  // Component for rendering item cards from MCP data
  const ItemCardComponent: React.FC<{ item: ItemCard }> = ({ item }) => {
    const handleCardClick = () => {
      // Navigate to item detail page
      navigate(`/items/${item._id}`);
      
      // Close the agent pane on mobile for better UX
      if (isMobile) {
        close();
      }
    };

    return (
      <div 
        className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-sm hover:shadow-lg dark:hover:shadow-slate-900/50 transition-all duration-200 p-4 mb-3 cursor-pointer hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/30 dark:hover:bg-blue-900/20 active:scale-[0.98] group"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        <div className="flex gap-3">
          {item.images && item.images.length > 0 && (
            <div className="w-16 h-16 flex-shrink-0">
              <img 
                src={item.images[0]} 
                alt={item.title}
                className="w-full h-full object-cover rounded-md"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <h4 className="font-medium text-gray-900 dark:text-slate-100 truncate pr-2">{item.title}</h4>
              <svg 
                className="w-4 h-4 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 flex-shrink-0 mt-0.5 transition-colors duration-200" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            
            {item.description && (
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1 line-clamp-2">{item.description}</p>
            )}
            
            <div className="flex items-center justify-between mt-2">
              <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                {item.currency === 'NZD' ? '$' : item.currency}
                {item.price.toLocaleString()}
                {item.currency === 'NZD' ? ' NZD' : ''}
              </span>
              <span className="text-xs text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded-full">
                {item.condition}
              </span>
            </div>
            
            {/* Location and quantity info */}
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-slate-400">
              {item.location && (
                <span>📍 {item.location.city}, {item.location.region}</span>
              )}
              {item.quantity && (
                <span>📦 {item.quantity} available</span>
              )}
            </div>
            
            {item.materials && item.materials.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {item.materials.slice(0, 3).map((material, index) => (
                  <span key={index} className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">
                    {material}
                  </span>
                ))}
                {item.materials.length > 3 && (
                  <span className="text-xs text-gray-500 dark:text-slate-400">
                    +{item.materials.length - 3} more
                  </span>
                )}
              </div>
            )}
            
            {item.project && (
              <div className="mt-2">
                <span className="text-xs text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/50 px-2 py-1 rounded">
                  Project: {item.project.title}
                </span>
              </div>
            )}
            
            {item.seller && (
              <div className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                Seller: {item.seller.name}
              </div>
            )}
            
            {/* Click hint */}
            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 font-medium transition-colors duration-200">
              Click to view details →
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Resize functionality for desktop
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMobile) return;
    setIsResizing(true);
    e.preventDefault();
  }, [isMobile]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || isMobile) return;
    const newWidth = window.innerWidth - e.clientX;
    setPaneWidth(Math.max(300, Math.min(800, newWidth))); // Min 300px, max 800px
  }, [isResizing, isMobile]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  if (!isOpen) return null;

  if (isMobile) {
    // Mobile: Full screen with input above bottom nav
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-900 z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 22.5l-.394-1.933a2.25 2.25 0 00-1.423-1.423L12.75 18.75l1.933-.394a2.25 2.25 0 001.423-1.423l.394-1.933.394 1.933a2.25 2.25 0 001.423 1.423l1.933.394-1.933.394a2.25 2.25 0 00-1.423 1.423z"/>
              </svg>
            </div>
            <h2 className="font-semibold">
              {location.pathname === '/marketplace' ? 'AI Material Search' : 'AI Assistant'}
            </h2>
          </div>
          <button
            onClick={close}
            className="btn-ghost p-2"
            aria-label="Close AI assistant"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages - takes up space above input and bottom nav */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32">
          {messages.length === 0 ? (
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                {location.pathname === '/marketplace' 
                  ? 'Hi! I can help you find materials and timber. Try asking:'
                  : 'Hi! I\'m your AI assistant. I can help you with:'
                }
              </p>
              <div className="space-y-2">
                {getContextualPrompts().map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handlePromptClick(prompt)}
                    className="w-full text-left p-3 text-sm bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  message.role === 'user' 
                    ? 'bg-primary-500 text-white' 
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input - Fixed above bottom nav */}
        <div className="fixed bottom-20 left-0 right-0 p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
          <div className="flex space-x-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(input)}
              placeholder={location.pathname === '/marketplace' ? 'Ask about timber, materials, prices...' : 'Ask me anything...'}
              className="flex-1 input text-sm"
              disabled={isLoading}
            />
            <button
              onClick={() => handleSendMessage(input)}
              disabled={!input.trim() || isLoading}
              className="btn-primary px-3 py-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Resizable pane on the right
  return (
    <>
      {/* Resize Handle */}
      <div
        ref={resizeRef}
        onMouseDown={handleMouseDown}
        className="w-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 cursor-col-resize transition-colors"
        style={{ height: '100%' }}
      />

      {/* Agent Pane */}
      <div 
        className="bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 flex flex-col"
        style={{ width: paneWidth }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h2 className="font-semibold">
              {location.pathname === '/marketplace' ? 'AI Material Search' : 'AI Assistant'}
            </h2>
          </div>
          <button
            onClick={close}
            className="btn-ghost p-2"
            aria-label="Close AI assistant"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {location.pathname === '/marketplace' 
                    ? 'Hi! I can help you find materials and timber. Try asking:'
                    : 'Hi! I\'m your AI assistant. I can help you with:'
                  }
                </p>
                <div className="space-y-2">
                  {getContextualPrompts().map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => handlePromptClick(prompt)}
                      className="w-full text-left p-3 text-sm bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-lg text-sm ${
                      message.role === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">
                      {message.content}
                      {message.isStreaming && (
                        <span className="inline-block w-2 h-5 bg-current ml-1 animate-pulse" />
                      )}
                    </div>
                    
                    {/* Show MCP data cards if available */}
                    {message.mcpData && message.mcpData.length > 0 && !message.isStreaming && (
                      <div className="mt-3 space-y-2">
                        <div className="text-sm font-medium border-t pt-2 opacity-75">
                          Found {message.mcpData.length} item{message.mcpData.length !== 1 ? 's' : ''}:
                        </div>
                        {message.mcpData.map((item: ItemCard, itemIndex: number) => (
                          <ItemCardComponent key={itemIndex} item={item} />
                        ))}
                      </div>
                    )}
                    
                    {/* Show tools used if available */}
                    {message.toolsUsed && message.toolsUsed.length > 0 && !message.isStreaming && (
                      <div className="mt-2 text-xs opacity-60 border-t pt-2">
                        <span className="font-medium">Tools used:</span> {message.toolsUsed.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(input)}
                placeholder={location.pathname === '/marketplace' ? 'Ask about timber, materials, prices...' : 'Ask me anything...'}
                className="flex-1 input text-sm"
                disabled={isLoading}
              />
              <button
                onClick={() => handleSendMessage(input)}
                disabled={!input.trim() || isLoading}
                className="btn-primary px-3 py-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
